import { matchesOrderingTrigger } from './ordering.js';
/**
 * 7 items = 5,040 permutations, which is instant and covers every ordering a teacher writes by
 * hand. Past that the enumeration stops being free, and it also stops being interesting: the
 * trigger vocabulary can cover at most a handful of permutations per state, so a block with 8+
 * items always has wrong answers left over unless someone authored tens of thousands of custom
 * distractors. Above the limit the derivation says "reachable" and marks itself non-exhaustive
 * rather than pretending to have checked.
 */
const ENUMERATION_LIMIT = 7;
const by = (states, trigger, reachable, cause) => {
    states.push(cause && !reachable ? { trigger, reachable, cause } : { trigger, reachable });
};
function permutations(ids) {
    if (ids.length <= 1)
        return [ids.slice()];
    const out = [];
    for (let i = 0; i < ids.length; i++) {
        const rest = [...ids.slice(0, i), ...ids.slice(i + 1)];
        for (const p of permutations(rest))
            out.push([ids[i], ...p]);
    }
    return out;
}
const eq = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
const rotations = (arr) => arr.map((_, k) => [...arr.slice(k), ...arr.slice(0, k)]);
/**
 * @param canonical the EFFECTIVE correct order — already resolved through any matched branch.
 *   `null` means branching is on and nothing resolved, the coverage gap where no order is correct.
 * @param itemIds every item on the block, in any order; the permutations are taken over these.
 */
export function orderingReachability(d, canonical, itemIds) {
    const states = [];
    const isCircular = d.shape === 'circular';
    const targeted = d.feedback.states.filter(s => s.type === 'targeted');
    if (d.gradingMode === 'optional') {
        by(states, 'any_response', true);
        for (const t of ['unanswered', 'correct', 'incorrect', 'any_answer'])
            by(states, t, false, 'mode');
        return { states, uncoveredWrong: [], exhaustive: true };
    }
    // `touched` is false until the student moves something, and that check runs first in both modes.
    by(states, 'unanswered', true);
    if (d.gradingMode === 'survey') {
        by(states, 'any_answer', itemIds.length > 0, itemIds.length > 0 ? undefined : 'nothing-to-pick');
        for (const t of ['correct', 'incorrect', 'any_response'])
            by(states, t, false, 'mode');
        return { states, uncoveredWrong: [], exhaustive: true };
    }
    by(states, 'any_answer', false, 'mode');
    by(states, 'any_response', false, 'mode');
    // No order to match: either the block has no items, or a branch set resolved to nothing. Every
    // other state past `unanswered` dies with it, since `isCorrect` needs a non-empty canonical and
    // the catch-all needs a submission to fall through.
    const noAnswerKey = !canonical || canonical.length === 0 || itemIds.length === 0;
    if (noAnswerKey) {
        by(states, 'correct', false, 'no-blanks');
        for (const s of targeted)
            by(states, s.trigger, false, 'no-blanks');
        by(states, 'incorrect', false, 'no-blanks');
        return { states, uncoveredWrong: [], exhaustive: true };
    }
    // The authored order IS the answer key, so Correct is always satisfiable from here.
    by(states, 'correct', true);
    if (itemIds.length > ENUMERATION_LIMIT) {
        for (const s of targeted)
            by(states, s.trigger, true);
        by(states, 'incorrect', true);
        return { states, uncoveredWrong: [], exhaustive: false };
    }
    const correctOrders = isCircular ? rotations(canonical) : [canonical];
    const isCorrect = (p) => correctOrders.some(c => eq(c, p));
    const wrong = permutations(itemIds).filter(p => !isCorrect(p));
    const covered = new Set();
    for (const s of targeted) {
        // Reachable when SOME wrong permutation matches it. A trigger whose only matches are correct
        // orders is dead, because the correct check is terminal and runs first — which is how
        // `reversed` dies on a two-item circular block, where the reversal is itself a rotation.
        let hit = false;
        wrong.forEach((p, i) => {
            if (matchesOrderingTrigger(s.trigger, p, canonical, isCircular)) {
                hit = true;
                covered.add(i);
            }
        });
        by(states, s.trigger, hit, hit ? undefined : 'fully-covered');
    }
    const uncoveredWrong = wrong.filter((_, i) => !covered.has(i));
    by(states, 'incorrect', uncoveredWrong.length > 0, uncoveredWrong.length > 0 ? undefined : 'fully-covered');
    return { states, uncoveredWrong, exhaustive: true };
}
//# sourceMappingURL=ordering-reachability.js.map